import React from 'react';
import {
  ClipboardCheck,
  Edit3,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
  Building,
  User,
  Calendar,
  FileText,
  Send,
  ThumbsUp,
  ThumbsDown,
  Shield,
  Zap,
  FileText as FilePdf
} from 'lucide-react';
import HSEAssessmentImageGallery from '../HSEAssessmentImageGallery';
import ImageModalViewer from '../ImageModalViewer';

const HSEAssessmentViewer = ({
  selectedAssessment,
  setActiveView,
  onEdit,
  onDelete,
  onSubmitForReview,
  onApprove,
  onReject,
  getStatusColor,
  getComplianceColor,
  isGeneratingPDF,
  handleDownloadPDF,
  user,
  handleImageUpload,
  handleImageDelete,
  properties,
  users,
  setAssessmentForm,
  setSelectedAssessment
}) => {
  console.log(selectedAssessment);
  // Assessment data loaded successfully
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center space-y-3 sm:space-y-0">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <ClipboardCheck className="mr-2 h-5 w-5 text-blue-500" />
            HSE Assessment Details
          </h2>
          <p className="text-gray-600 mt-1">{selectedAssessment.tenant_name}</p>
        </div>
        <div className="flex flex-col sm:flex-row w-full sm:w-auto space-y-2 sm:space-y-0 sm:space-x-2">
          {(user?.role?.value === 'Admin' || user?.role?.value === 'Property Manager') && selectedAssessment.status === 'SUBMITTED' && (
            <>
              <button
                onClick={() => onApprove(selectedAssessment.id)}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center justify-center w-full sm:w-auto"
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                Approve
              </button>
              <button
                onClick={() => onReject(selectedAssessment.id)}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 flex items-center justify-center w-full sm:w-auto"
              >
                <ThumbsDown className="w-4 h-4 mr-2" />
                Reject
              </button>
            </>
          )}
          <button
            onClick={() => setActiveView('list')}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 min-h-[44px] flex items-center justify-center w-full sm:w-auto"
          >
            ← Back to List
          </button>
          <button
            onClick={handleDownloadPDF}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center w-full sm:w-auto disabled:opacity-50"
            disabled={isGeneratingPDF}
          >
            {isGeneratingPDF ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Generating...
              </>
            ) : (
              <>
                <FilePdf className="w-4 h-4 mr-2" />
                Export PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Assessment Details */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <div className="flex items-center space-x-2">
            <Building className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Building</p>
              <p className="font-medium">
                {properties.find(p => p.id === selectedAssessment.building_id)?.building_name ||
                 properties.find(p => p.id === selectedAssessment.building_id)?.buildingName ||
                 selectedAssessment.building_id}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Auditor</p>
              <p className="font-medium">{selectedAssessment.auditor_name}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <div>
              <p className="text-sm text-gray-500">Assessment Date</p>
              <p className="font-medium">{new Date(selectedAssessment.assessment_date).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Tenant Information */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Building className="w-5 h-5 mr-2 text-blue-500" />
            Tenant Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Tenant Name</p>
              <p className="font-medium">{selectedAssessment.tenant_name}</p>
            </div>
            {selectedAssessment.employee_count && (
              <div>
                <p className="text-sm text-gray-500">Employee Count</p>
                <p className="font-medium">{selectedAssessment.employee_count}</p>
              </div>
            )}
            {selectedAssessment.tenant_representative && (
              <div>
                <p className="text-sm text-gray-500">Tenant Representative</p>
                <p className="font-medium">{selectedAssessment.tenant_representative}</p>
              </div>
            )}
          </div>
        </div>

        {/* Status and Score */}
        <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center space-x-4">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedAssessment.status)}`}>
              {selectedAssessment.status}
            </span>
            <div className="text-center">
              <p className="text-sm text-gray-500">Compliance Score</p>
              <p className={`text-2xl font-bold ${getComplianceColor(selectedAssessment.overall_compliance_score)}`}>
                {selectedAssessment.overall_compliance_score}%
              </p>
            </div>
          </div>
          {selectedAssessment.follow_up_required && (
            <div className="text-right">
              <p className="text-sm text-gray-500">Follow-up Required</p>
              <p className="font-medium text-orange-600">
                {new Date(selectedAssessment.follow_up_date).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {/* Legal Wall Charts */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <FileText className="w-5 h-5 mr-2 text-green-500" />
            Legal Wall Charts
          </h3>
          <div className="space-y-4">
            {[
              { key: 'ohs_act', label: 'OHS Act' },
              { key: 'labour_relations_act', label: 'Labour Relations Act' },
              { key: 'basic_conditions_employment', label: 'Basic Conditions of Employment' },
              { key: 'coida', label: 'COIDA' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fire Fighting Equipment */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
            Fire Fighting Equipment
          </h3>
          <div className="space-y-4">
            {[
              { key: 'fire_extinguishers', label: 'Fire Extinguishers' },
              { key: 'fire_hose_reels', label: 'Fire Hose Reels' },
              { key: 'fire_blankets', label: 'Fire Blankets' },
              { key: 'emergency_lighting', label: 'Emergency Lighting' },
              { key: 'fire_pump', label: 'Fire Pump' },
              { key: 'fire_hydrants', label: 'Fire Hydrants' },
              { key: 'fire_suppression_system', label: 'Fire Suppression System' },
              { key: 'fire_safety_signage', label: 'Fire Safety Signage' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Automatic Sprinkler System */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-blue-500" />
            Automatic Sprinkler System
          </h3>
          <div className="space-y-4">
            {[
              { key: 'sprinkler_system_serviced', label: 'Sprinkler system serviced' },
              { key: 'sprinkler_valves_locked_chain_padlock', label: 'Sprinkler valves locked with chain and padlock' },
              { key: 'sprinkler_valve_chamber_spare_parts', label: 'Sprinkler Valve Chamber - Spare parts, thread tape and spanner available' },
              { key: 'sprinkler_valves_labelled', label: 'Sprinkler Valves are labelled' },
              { key: 'sprinkler_valve_chamber_schematic', label: 'Sprinkler Valve Chamber schematic chart available' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Evacuation Routes & Exits */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-orange-500" />
            Evacuation Routes & Exits
          </h3>
          <div className="space-y-4">
            {[
              { key: 'escape_doors_fire_exit_signage', label: 'Escape doors on the outside have "Fire Exit Keep Clear" signage displayed' },
              { key: 'fire_escape_plans_displayed', label: 'Fire Escape Plans displayed in the workplace in accordance with SANS 23601' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Preparedness */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Shield className="w-5 h-5 mr-2 text-orange-500" />
            Emergency Preparedness
          </h3>
          <div className="space-y-4">
            {[
              { key: 'emergency_assembly_points', label: 'Emergency Assembly Points' },
              { key: 'emergency_contact_list', label: 'Emergency Contact List' },
              { key: 'emergency_procedures', label: 'Emergency Procedures' },
              { key: 'first_aid_kits', label: 'First Aid Kits' },
              { key: 'emergency_communication', label: 'Emergency Communication' },
              { key: 'emergency_drills', label: 'Emergency Drills' },
              { key: 'emergency_exits', label: 'Emergency Exits' },
              { key: 'emergency_wardens', label: 'Emergency Wardens' },
              { key: 'backup_power', label: 'Backup Power' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Electrical DB */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-yellow-500" />
            Electrical DB
          </h3>
          <div className="space-y-4">
            {[
              { key: 'electrical_installations', label: 'Electrical Installations' },
              { key: 'electrical_testing', label: 'Electrical Testing' },
              { key: 'electrical_safety_switches', label: 'Electrical Safety Switches' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Hazardous Chemical Agents (HCA) */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
            Hazardous Chemical Agents (HCA)
          </h3>
          <div className="space-y-4">
            {[
              { key: 'chemical_storage', label: 'Chemical Storage' },
              { key: 'chemical_handling_procedures', label: 'Chemical Handling Procedures' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Stacking of Articles */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-purple-500" />
            Stacking of Articles
          </h3>
          <div className="space-y-4">
            {[
              { key: 'stacking_height_limits', label: 'Stacking Height Limits' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Housekeeping */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-green-500" />
            Housekeeping
          </h3>
          <div className="space-y-4">
            {[
              { key: 'housekeeping', label: 'Good housekeeping principals applied' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* General Condition */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-purple-500" />
            General Condition
          </h3>
          <div className="space-y-4">
            {[
              { key: 'floors', label: 'Floors' },
              { key: 'walls', label: 'Walls' },
              { key: 'ceilings', label: 'Ceilings' },
              { key: 'windows', label: 'Windows' },
              { key: 'doors', label: 'Doors' },
              { key: 'stairways', label: 'Stairways' },
              { key: 'lighting', label: 'Lighting' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Signage Displayed */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-blue-500" />
            Signage Displayed
          </h3>
          <div className="space-y-4">
            {[
              { key: 'signage_displayed', label: 'Signage displayed' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Fire Detection and Alarm Systems */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 text-red-500" />
            Fire Detection and Alarm Systems
          </h3>
          <div className="space-y-4">
            {[
              { key: 'fire_detection_serviced_tested_standalone', label: 'Fire detection and alarm systems serviced and tested, no faults on panels' },
              { key: 'fire_detection_operating_procedures_standalone', label: 'Fire detection and alarm systems - operating procedures available at the control panel' },
              { key: 'fire_alarm_call_points_signage_standalone', label: 'Fire alarm call points clearly identifiable with signage in accordance with SANS 1186-5' },
              { key: 'dust_covers_removed_standalone', label: 'Dust covers removed from smoke detectors' }
            ].map(item => (
              <div key={item.key} className="bg-white border border-gray-200 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{item.label}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    selectedAssessment[`${item.key}_compliance`] === 'compliant' ? 'bg-green-100 text-green-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'non_compliant' ? 'bg-red-100 text-red-800' :
                    selectedAssessment[`${item.key}_compliance`] === 'partial' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedAssessment[`${item.key}_compliance`]?.replace('_', ' ')}
                  </span>
                </div>
                {selectedAssessment[`${item.key}_comments`] && (
                  <p className="text-sm text-gray-600">{selectedAssessment[`${item.key}_comments`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        {selectedAssessment.recommendations && (
          <div className="mb-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Recommendations</h3>
            <p className="text-gray-700 bg-blue-50 p-4 rounded-lg">{selectedAssessment.recommendations}</p>
          </div>
        )}

        {/* Assessment Images - New Categorized Gallery */}
        {(((selectedAssessment.images && selectedAssessment.images.length > 0) ||
           (selectedAssessment.categorized_images && Object.keys((selectedAssessment.categorized_images?.images) || selectedAssessment.categorized_images || {}).length > 0))) && (
          <div className="mb-6">
            <HSEAssessmentImageGallery
              assessmentId={selectedAssessment.id}
              images={selectedAssessment.images || []}
              categorizedImages={selectedAssessment.categorized_images || {}}
              onImageUpload={handleImageUpload}
              onImageDelete={handleImageDelete}
              canEdit={selectedAssessment.created_by === user?.id && selectedAssessment.status === 'DRAFT'}
              className="bg-white border border-gray-200 rounded-lg"
            />
          </div>
        )}

        {/* Assessment Metadata */}
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Clock className="w-5 h-5 mr-2 text-gray-500" />
            Assessment History
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500">Created By</p>
                <p className="font-medium">
                  {(() => {
                    if (selectedAssessment.created_by_name) {
                      return selectedAssessment.created_by_name;
                    }
                    const creator = (Array.isArray(users) ? users : []).find(u => u.id === selectedAssessment.created_by);
                    return creator ? `${creator.name} ${creator.surname}` : selectedAssessment.created_by;
                  })()}
                </p>
                <p className="text-xs text-gray-400">{new Date(selectedAssessment.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500">Last Updated</p>
                <p className="text-xs text-gray-400">{new Date(selectedAssessment.updated_at).toLocaleString()}</p>
              </div>
              {selectedAssessment.submitted_at && (
                <div>
                  <p className="text-gray-500">Submitted</p>
                  <p className="text-xs text-gray-400">{new Date(selectedAssessment.submitted_at).toLocaleString()}</p>
                </div>
              )}
              {selectedAssessment.reviewed_by && (
                <div>
                  <p className="text-gray-500">Reviewed By</p>
                  <p className="font-medium">
                    {(() => {
                      if (selectedAssessment.reviewed_by_name) {
                        return selectedAssessment.reviewed_by_name;
                      }
                      const reviewer = (Array.isArray(users) ? users : []).find(u => u.id === selectedAssessment.reviewed_by);
                      return reviewer ? `${reviewer.name} ${reviewer.surname}` : selectedAssessment.reviewed_by;
                    })()}
                  </p>
                  {selectedAssessment.reviewed_at && (
                    <p className="text-xs text-gray-400">{new Date(selectedAssessment.reviewed_at).toLocaleString()}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action Buttons for Assessment Owner */}
        {selectedAssessment.created_by === user?.id && selectedAssessment.status === 'DRAFT' && (
          <div className="flex space-x-4 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setSelectedAssessment(selectedAssessment);
                // Format the assessment_date for the date input field
                const formattedAssessment = {
                  ...selectedAssessment,
                  assessment_date: selectedAssessment.assessment_date ?
                    new Date(selectedAssessment.assessment_date).toISOString().split('T')[0] :
                    new Date().toISOString().split('T')[0]
                };
                setAssessmentForm(formattedAssessment);
                setActiveView('edit');
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
            >
              <Edit3 className="w-4 h-4 mr-2" />
              Edit
            </button>
            <button
              onClick={() => onSubmitForReview(selectedAssessment.id)}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center"
            >
              <Send className="w-4 h-4 mr-2" />
              Submit for Review
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HSEAssessmentViewer;
